/**
 * WorkflowGuardGate - Explica a trava do processo antes do clique
 *
 * Envolve um botão de ação protegida. Quando a política do processo devolve
 * DENY, o gatilho visual desabilita o conteúdo e explica o motivo; em WARN o
 * botão continua ativo, apenas com o aviso.
 *
 * UX (v1.1.0): além do tooltip de hover (desktop), é exibido ao lado um ícone
 * de alerta CLICÁVEL que abre um popover com o motivo completo. Esse é o padrão
 * usado por SaaS de grande porte para tornar a explicação acessível também no
 * toque (mobile/tablet), onde hover não existe.
 *
 * Camada de apresentação: a decisão continua sendo do banco.
 *
 * @version 1.1.0
 * RELEASE: 2026-09-03 v1.1.0 - Ícone de alerta clicável + popover com o motivo,
 *          acessível por toque; cores semânticas (destrutiva/aviso).
 * RELEASE: 2026-09-03 v1.0.0 - Criação: feedback antecipado da trava.
 */

import type { ReactNode } from 'react';
import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { WorkflowGuardDecision } from '@/hooks/useWorkflowGuardCheck';

interface Props {
  /** Decisão vinda de `useWorkflowGuardCheck` */
  decisao: WorkflowGuardDecision;
  /** Botão (ou grupo) a ser protegido visualmente */
  children: ReactNode;
  /** Lado do tooltip/popover */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Oculta o ícone de alerta (usar apenas em espaços muito estreitos) */
  ocultarIcone?: boolean;
}

const MENSAGEM_PADRAO =
  'Existe uma pendência do processo neste registro. Resolva a aprovação/exigência para liberar esta ação.';

export function WorkflowGuardGate({ decisao, children, side = 'bottom', ocultarIcone = false }: Props) {
  const [aberto, setAberto] = useState(false);

  // Nada a explicar: devolve o conteúdo intacto (sem custo de tooltip)
  if (!decisao.bloqueado && !decisao.avisa) return <>{children}</>;

  const mensagem = decisao.mensagem || MENSAGEM_PADRAO;
  const titulo = decisao.bloqueado ? 'Ação bloqueada pelo processo' : 'Atenção do processo';

  return (
    <TooltipProvider>
      <span className="inline-flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            {/*
             * O wrapper mantém o tooltip acessível mesmo com o botão desabilitado
             * (elemento desabilitado não emite eventos de mouse).
             */}
            <span
              className={
                decisao.bloqueado
                  ? 'inline-flex cursor-not-allowed [&>*]:pointer-events-none [&>*]:opacity-50'
                  : 'inline-flex'
              }
            >
              {children}
            </span>
          </TooltipTrigger>
          <TooltipContent side={side} className="max-w-[280px]">
            <p className="text-xs leading-5">{mensagem}</p>
          </TooltipContent>
        </Tooltip>

        {/* Afordance por toque: ícone clicável que abre a explicação completa */}
        {!ocultarIcone && (
          <Popover open={aberto} onOpenChange={setAberto}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Ver motivo do bloqueio"
                className={cn(
                  'h-6 w-6 shrink-0 rounded-full',
                  decisao.bloqueado
                    ? 'text-destructive hover:bg-destructive/10 hover:text-destructive'
                    : 'text-warning hover:bg-warning/10 hover:text-warning',
                )}
              >
                <AlertTriangle className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent side={side} align="end" className="w-[280px] p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle
                  className={cn(
                    'mt-0.5 h-4 w-4 shrink-0',
                    decisao.bloqueado ? 'text-destructive' : 'text-warning',
                  )}
                />
                <div className="min-w-0 space-y-1">
                  <p className="text-xs font-semibold text-foreground">{titulo}</p>
                  <p className="text-xs leading-5 text-muted-foreground">{mensagem}</p>
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAberto(false)}>
                  Entendi
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </span>
    </TooltipProvider>
  );
}
